import { useState, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import axios from '../../services/AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';

// Centralized step configuration
export const PAYROLL_STEPS = {
  MANAGE_PAYSLIPS: {
    id: 'step_1_manage_payslips',
    number: 1,
    title: 'Lönespecifikationer',
    description: 'Skapa och hantera lönebesked',
    icon: 'Users',
    requirements: {
      canEnter: () => true,
      canExit: (payroll) => payroll?.isLocked,
      exitMessage: 'Du måste låsa perioden för att fortsätta'
    },
    undo: {
      canUndo: () => false,
      action: null
    }
  },
  PAY_AND_BOOKKEEPING: {
    id: 'step_2_pay_and_bookkeeping',
    number: 2,
    title: 'Betala & Bokför',
    description: 'Utbetalning och bokföring',
    icon: 'CreditCard',
    requirements: {
      canEnter: (payroll) => payroll?.isLocked,
      canExit: (payroll, payslips) => {
        // Allow exit without verification if there are no payslips (nothing to book)
        if (payslips?.length === 0) return true;
        return payroll?.verificationCreated;
      },
      exitMessage: 'Du måste skapa bokföringsverifikat för att fortsätta'
    },
    undo: {
      canUndo: (payroll) => payroll?.verificationCreated && !payroll?.agiSubmitted,
      action: {
        type: 'undo_verification',
        endpoint: 'verification',
        method: 'DELETE',
        warning: 'OBS: Om AGI redan är skickat till Skatteverket måste du ångra det först.'
      }
    }
  },
  AGI_DECLARATION: {
    id: 'step_3_agi_declaration',
    number: 3,
    title: 'AGI Deklaration',
    description: 'Skapa och skicka AGI-fil',
    icon: 'FileText',
    requirements: {
      canEnter: (payroll, payslips) => {
        // If no payslips, only require locked status (no verification needed)
        if (payslips?.length === 0) return payroll?.isLocked;
        return payroll?.isLocked && payroll?.verificationCreated;
      },
      canExit: (payroll) => payroll?.agiSubmitted,
      exitMessage: 'Du måste markera AGI som skickad för att fortsätta'
    },
    undo: {
      canUndo: (payroll) => payroll?.agiSubmitted && !payroll?.taxesPaid,
      action: {
        type: 'undo_agi',
        endpoint: 'undo-agi',
        method: 'POST',
        warning: 'OBS: Om AGI redan skickats till Skatteverket måste du hantera det manuellt!'
      }
    }
  },
  PAY_TAXES: {
    id: 'step_4_pay_taxes',
    number: 4,
    title: 'Betala Skatter',
    description: 'Inbetalning till Skatteverket',
    icon: 'Calculator',
    requirements: {
      canEnter: (payroll, payslips) => {
        // If no payslips, only require AGI submitted (no verification needed)
        if (payslips?.length === 0) return payroll?.agiSubmitted;
        return payroll?.verificationCreated && payroll?.agiSubmitted;
      },
      canExit: (payroll) => payroll?.taxesPaid,
      exitMessage: 'Du måste markera skatter som betalda för att fortsätta'
    },
    undo: {
      canUndo: (payroll) => payroll?.taxesPaid && !payroll?.withdrawalBooked,
      action: {
        type: 'undo_taxes',
        endpoint: 'undo-taxes',
        method: 'POST',
        warning: 'OBS: Detta påverkar inte faktiska betalningar!'
      }
    }
  },
  BOOK_WITHDRAWAL: {
    id: 'step_5_book_withdrawal',
    number: 5,
    title: 'Bokför Uttag',
    description: 'Bokför Skatteverkets uttag',
    icon: 'BookOpen',
    requirements: {
      canEnter: (payroll, payslips) => {
        // If no payslips, no withdrawal to book (0 kr taxes)
        if (payslips?.length === 0) return payroll?.taxesPaid;
        return payroll?.taxesPaid;
      },
      canExit: (payroll, payslips) => {
        // If no payslips, no withdrawal to book - can skip this step
        if (payslips?.length === 0) return true;
        return payroll?.withdrawalBooked;
      },
      exitMessage: 'Du måste bokföra Skatteverkets uttag för att fortsätta'
    },
    undo: {
      canUndo: (payroll) => payroll?.withdrawalBooked,
      action: {
        type: 'undo_withdrawal',
        endpoint: 'undo-withdrawal',
        method: 'POST',
        warning: 'OBS: Detta påverkar inte faktiska betalningar!'
      }
    }
  },
  OVERVIEW: {
    id: 'step_6_overview',
    number: 6,
    title: 'Översikt',
    description: 'Sammanfattning och rapporter',
    icon: 'TrendingUp',
    requirements: {
      canEnter: (payroll, payslips) => {
        // If no payslips, no withdrawal needed - can enter after taxes paid
        if (payslips?.length === 0) return payroll?.taxesPaid;
        return payroll?.withdrawalBooked;
      },
      canExit: () => false, // Final step
      exitMessage: null
    },
    undo: {
      canUndo: () => false,
      action: null
    }
  }
};

// Step order for navigation
const STEP_ORDER = Object.values(PAYROLL_STEPS).sort((a, b) => a.number - b.number);

export const usePayrollFlow = (payroll, payslips, onStepChange, onUndoAction, companyId, onRefreshData) => {
  const [activeStep, setActiveStep] = useState(PAYROLL_STEPS.MANAGE_PAYSLIPS.id);
  const [stepOperationInProgress, setStepOperationInProgress] = useState(false);

  // Get current step configuration
  const currentStepConfig = useMemo(() => 
    PAYROLL_STEPS[Object.keys(PAYROLL_STEPS).find(key => PAYROLL_STEPS[key].id === activeStep)] || PAYROLL_STEPS.MANAGE_PAYSLIPS,
    [activeStep]
  );

  // Get current step index
  const currentStepIndex = useMemo(() => 
    STEP_ORDER.findIndex(step => step.id === activeStep),
    [activeStep]
  );

  // Navigation helpers
  const canProceedToNext = useMemo(() => {
    const nextStep = STEP_ORDER[currentStepIndex + 1];
    return nextStep ? nextStep.requirements.canEnter(payroll, payslips) : false;
  }, [currentStepIndex, payroll, payslips]);

  const canGoBack = useMemo(() => {
    return currentStepIndex > 0;
  }, [currentStepIndex]);

  const getNextStep = useCallback(() => {
    return STEP_ORDER[currentStepIndex + 1] || null;
  }, [currentStepIndex]);

  const getPreviousStep = useCallback(() => {
    return STEP_ORDER[currentStepIndex - 1] || null;
  }, [currentStepIndex]);

  // Step validation
  const canEnterStep = useCallback((stepId) => {
    const stepConfig = Object.values(PAYROLL_STEPS).find(step => step.id === stepId);
    return stepConfig ? stepConfig.requirements.canEnter(payroll, payslips) : false;
  }, [payroll, payslips]);

  const canExitCurrentStep = useCallback(() => {
    return currentStepConfig.requirements.canExit(payroll, payslips);
  }, [currentStepConfig, payroll, payslips]);

  const getExitMessage = useCallback(() => {
    return currentStepConfig.requirements.exitMessage;
  }, [currentStepConfig]);

  // Undo logic - Check what work was done to enter the current step
  const getRequiredUndo = useCallback((fromStepId, toStepId) => {
    console.log('DEBUG: getRequiredUndo called with:', { fromStepId, toStepId, payroll });

    const fromStep = Object.values(PAYROLL_STEPS).find(step => step.id === fromStepId);
    const toStep = Object.values(PAYROLL_STEPS).find(step => step.id === toStepId);

    console.log('DEBUG: Found steps:', { fromStep, toStep });
    
    if (!fromStep || !toStep || fromStep.number <= toStep.number) {
      console.log('DEBUG: No undo required - invalid steps or not going backward');
      return null;
    }

    // When going backward, we need to undo work that was done to ENTER the current step
    // For example: Going Step 4 → Step 3 should undo AGI (which was required to enter Step 4)
    
    // Special cases: Undo work that was done to ENTER the current step
    
    // Going from Step 4 → Step 3: Need to undo AGI (work done when entering Step 4)
    if (fromStepId === PAYROLL_STEPS.PAY_TAXES.id && toStepId === PAYROLL_STEPS.AGI_DECLARATION.id) {
      // Check if AGI is submitted (work that got us to Step 4)
      if (payroll?.agiSubmitted) {
        console.log('DEBUG: Going Step 4 → 3, need to undo AGI');
        const agiStep = PAYROLL_STEPS.AGI_DECLARATION;
        return {
          type: agiStep.undo.action.type,
          endpoint: agiStep.undo.action.endpoint,
          method: agiStep.undo.action.method,
          data: agiStep.undo.action.data,
          warning: agiStep.undo.action.warning,
          fromStep: fromStepId,
          toStep: toStepId,
          stepTitle: agiStep.title
        };
      }
    }
    
    // Going from Step 5 (BOOK_WITHDRAWAL) → Step 4 (PAY_TAXES): Need to undo taxes (work done when entering Step 5)
    if (fromStepId === PAYROLL_STEPS.BOOK_WITHDRAWAL.id && toStepId === PAYROLL_STEPS.PAY_TAXES.id) {
      // Check if taxes are paid (work that got us to Step 5)
      if (payroll?.taxesPaid) {
        console.log('DEBUG: Going Step 5 → 4, need to undo taxes');
        const taxStep = PAYROLL_STEPS.PAY_TAXES;
        return {
          type: taxStep.undo.action.type,
          endpoint: taxStep.undo.action.endpoint,
          method: taxStep.undo.action.method,
          data: taxStep.undo.action.data,
          warning: taxStep.undo.action.warning,
          fromStep: fromStepId,
          toStep: toStepId,
          stepTitle: taxStep.title
        };
      }
    }
    
    // Going from Step 6 (OVERVIEW) → Step 5 (BOOK_WITHDRAWAL): Need to undo withdrawal (work done when entering Step 6)
    if (fromStepId === PAYROLL_STEPS.OVERVIEW.id && toStepId === PAYROLL_STEPS.BOOK_WITHDRAWAL.id) {
      // Check if withdrawal is booked (work that got us to Step 6)
      if (payroll?.withdrawalBooked) {
        console.log('DEBUG: Going Step 6 → 5, need to undo withdrawal');
        const withdrawalStep = PAYROLL_STEPS.BOOK_WITHDRAWAL;
        return {
          type: withdrawalStep.undo.action.type,
          endpoint: withdrawalStep.undo.action.endpoint,
          method: withdrawalStep.undo.action.method,
          data: withdrawalStep.undo.action.data,
          warning: withdrawalStep.undo.action.warning,
          fromStep: fromStepId,
          toStep: toStepId,
          stepTitle: withdrawalStep.title
        };
      }
    }
    
    // Also check if the current step (fromStep) has its own work that needs undoing
    if (fromStep.undo && fromStep.undo.canUndo && fromStep.undo.canUndo(payroll)) {
      console.log('DEBUG: FROM step has work that can and should be undone');
      
      const undoAction = fromStep.undo.action;
      if (!undoAction) {
        console.log('DEBUG: canUndo is true but no action defined');
        return null;
      }

      return {
        type: undoAction.type,
        endpoint: undoAction.endpoint,
        method: undoAction.method,
        data: undoAction.data,
        warning: undoAction.warning,
        fromStep: fromStepId,
        toStep: toStepId,
        stepTitle: fromStep.title
      };
    }

    console.log('DEBUG: No undo required for this transition');
    return null;
  }, [payroll]);


  // Navigate to step for viewing only (no backend update)
  const navigateToStep = useCallback((targetStepId) => {
    // Check if step is accessible for viewing
    const targetStep = Object.values(PAYROLL_STEPS).find(step => step.id === targetStepId);
    if (!targetStep) return;

    // Can view a step if its requirements are met
    if (!targetStep.requirements.canEnter(payroll, payslips)) {
      Swal.fire({
        icon: 'info',
        title: 'Steg inte tillgängligt',
        text: 'Du måste slutföra tidigare steg innan du kan visa detta steg.',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Just update the viewing state - no backend call
    setActiveStep(targetStepId);
  }, [payroll, payslips]);

  // Step change handler - for actual progression with backend update
  const handleStepChange = useCallback(async (targetStepId, direction = 'forward') => {
    if (stepOperationInProgress) return;

    const targetStep = Object.values(PAYROLL_STEPS).find(step => step.id === targetStepId);
    if (!targetStep) return;

    // Forward validation
    if (direction === 'forward' && !targetStep.requirements.canEnter(payroll, payslips)) {
      Swal.fire({
        icon: 'warning',
        title: 'Kan inte fortsätta',
        text: targetStep.requirements.exitMessage || 'Krav för detta steg är inte uppfyllda.'
      });
      return;
    }

    // Backward validation logic
    if (direction === 'backward') {
      // Simple backward navigation only - no automatic undo
      // If work needs to be undone, the backward button will be disabled
      // User must use in-step undo button first
    }

    // Execute step change with backend update
    setStepOperationInProgress(true);
    try {
      if (onStepChange) {
        await onStepChange(targetStepId);
      }
      setActiveStep(targetStepId);
    } catch (error) {
      console.error('Error changing step:', error);
      Swal.fire({
        icon: 'error',
        title: 'Fel',
        text: 'Det gick inte att uppdatera steget'
      });
    } finally {
      setStepOperationInProgress(false);
    }
  }, [activeStep, payroll, payslips, stepOperationInProgress, getRequiredUndo, onStepChange, onUndoAction]);

  // Check if backward navigation is blocked (for disabling button)
  const isBackwardBlocked = useCallback(() => {
    // Block backward navigation if current step has completed work that hasn't been undone
    // User must use in-step undo button first, then can go back

    // Step 2: Block if verification created
    if (activeStep === PAYROLL_STEPS.PAY_AND_BOOKKEEPING.id) {
      if (payroll?.verificationCreated) {
        return true;
      }
    }

    // Step 3: Block if AGI submitted
    if (activeStep === PAYROLL_STEPS.AGI_DECLARATION.id) {
      if (payroll?.agiSubmitted) {
        return true;
      }
    }

    // Step 4: Block if taxes paid
    if (activeStep === PAYROLL_STEPS.PAY_TAXES.id) {
      if (payroll?.taxesPaid) {
        return true;
      }
    }

    // Step 5: Block if withdrawal booked
    if (activeStep === PAYROLL_STEPS.BOOK_WITHDRAWAL.id) {
      if (payroll?.withdrawalBooked) {
        return true;
      }
    }

    return false;
  }, [activeStep, payroll]);

  // Get message explaining why backward is blocked
  const getBackwardBlockMessage = useCallback(() => {
    // Return appropriate message for each step

    if (activeStep === PAYROLL_STEPS.PAY_AND_BOOKKEEPING.id && payroll?.verificationCreated) {
      return 'Du måste först ångra bokföringsverifikatet';
    }

    if (activeStep === PAYROLL_STEPS.AGI_DECLARATION.id && payroll?.agiSubmitted) {
      return 'Du måste först ångra AGI-deklarationen';
    }

    if (activeStep === PAYROLL_STEPS.PAY_TAXES.id && payroll?.taxesPaid) {
      return 'Du måste först ångra skattebetalningen';
    }

    if (activeStep === PAYROLL_STEPS.BOOK_WITHDRAWAL.id && payroll?.withdrawalBooked) {
      return 'Du måste först ångra bokföringen av uttag';
    }

    return null;
  }, [activeStep, payroll]);

  // Navigation actions
  const goToNextStep = useCallback(() => {
    const nextStep = getNextStep();
    if (nextStep) {
      handleStepChange(nextStep.id, 'forward');
    }
  }, [getNextStep, handleStepChange]);

  const goToPreviousStep = useCallback(() => {
    const previousStep = getPreviousStep();
    if (previousStep) {
      handleStepChange(previousStep.id, 'backward');
    }
  }, [getPreviousStep, handleStepChange]);

  // Step progress calculation
  const getStepProgress = useCallback(() => {
    const totalSteps = STEP_ORDER.length;
    const completedSteps = STEP_ORDER.filter(step => {
      switch (step.id) {
        case PAYROLL_STEPS.MANAGE_PAYSLIPS.id:
          return payroll?.isLocked;
        case PAYROLL_STEPS.PAY_AND_BOOKKEEPING.id:
          return payroll?.verificationCreated;
        case PAYROLL_STEPS.AGI_DECLARATION.id:
          return payroll?.agiSubmitted;
        case PAYROLL_STEPS.PAY_TAXES.id:
          return payroll?.taxesPaid;
        case PAYROLL_STEPS.OVERVIEW.id:
          return payroll?.agiSubmitted && payroll?.taxesPaid;
        default:
          return false;
      }
    }).length;

    return {
      completed: completedSteps,
      total: totalSteps,
      percentage: Math.round((completedSteps / totalSteps) * 100)
    };
  }, [payroll]);

  return {
    // State
    activeStep,
    stepOperationInProgress,
    currentStepConfig,
    currentStepIndex,
    
    // Navigation
    canProceedToNext,
    canGoBack,
    getNextStep,
    getPreviousStep,
    isBackwardBlocked,
    getBackwardBlockMessage,
    
    // Validation
    canEnterStep,
    canExitCurrentStep,
    getExitMessage,
    
    // Actions
    navigateToStep,        // View-only navigation (no backend update)
    handleStepChange,      // Progress navigation (with backend update)
    goToNextStep,
    goToPreviousStep,
    setActiveStep,
    
    // Progress
    getStepProgress,
    
    // Constants
    STEPS: PAYROLL_STEPS,
    STEP_ORDER
  };
};
